import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useCallback, useEffect, } from 'react';
import * as _ from 'lodash';
import * as graph from '@adc/sdk';
import { Text } from '../components/elementals';
import { CurrencyInputField, DropdownField, MaskedInputField, CheckboxField, } from '../components/formFields';
import * as constants from '../constants';
import * as utils from '.';
import AdditionalNotesField from '../components/onlineAuction/sealedBid/bidForm/additionalNotesField';
const { formatMoney } = utils.formatters;
const { ColorNames } = constants;
const { paymentTypeOptions, sellerBuyerOptions } = utils.traditionalOffer;
export const getSealedBidFormFields = (viewingFrom = graph.enums.CurrentTargetAudience.Seller) => {
    const fields = [
        {
            name: 'offer_amount',
            label: 'Purchase Price',
            toolTipContent: 'The amount a buyer agrees to pay for a property as stated in the purchase agreement.',
        },
        {
            name: 'down_payment_amount',
            label: 'Down Payment',
            toolTipContent: 'A down payment is the portion of a property’s purchase price that a buyer pays upfront when closing on a real estate loan.',
        },
        {
            name: 'earnest_money_deposit_amount',
            label: 'Earnest Money Deposit',
            toolTipContent: 'A deposit placed by the buyer to let the seller know that the buyer is earnest or serious about buying the property.',
        },
        {
            name: 'payment_type',
            label: 'Payment Type',
            toolTipContent: 'The method in which the buyer intends to pay for the property.',
        },
        {
            name: 'seller_concession_amount',
            label: 'Seller Paid Closing Costs',
            toolTipContent: 'The amount the buyer is requesting the seller to contribute towards the buyer closing costs.',
        },
        {
            name: 'agent_commission_amount',
            label: `Buyer's Agent Commission`,
            toolTipContent: viewingFrom === graph.enums.CurrentTargetAudience.Buyer
                ? 'The fee paid to the real estate agent who represents the buyer on the sale.'
                : 'The amount the buyer is asking the seller to pay to the buyer’s agent as a commission.',
        },
        {
            name: 'paid_by',
            label: 'BA Commission Paid By',
            toolTipContent: '',
        },
        {
            name: 'close_escrow_duration',
            label: 'Closing Date',
            toolTipContent: 'The number of days agreed upon to complete the sale after the contract is signed.',
        },
        {
            name: 'leaseback',
            label: 'Rentback',
            toolTipContent: 'The rentback period is the number of days the seller is requesting to stay in the property after the sale closes.',
        },
        {
            name: 'inspection_contingency_period_duration',
            label: 'Inspection Contingency',
            toolTipContent: 'A provision allowing the buyer to have the property inspected and cancel the contract without penalty based on the inspection results.',
        },
        {
            name: 'option_period',
            label: 'Option Period',
            toolTipContent: 'The time during which buyers can conduct inspections and decide whether to move forward with the purchase. If the buyer does not proceed to closing, the Option Fee is retained by the seller.',
        },
        {
            name: 'financing_contingency_duration',
            label: 'Financing Contingency',
            toolTipContent: 'A provision allowing the buyer to cancel the contract if they are unable to secure financing by a specified timeframe.',
        },
        {
            name: 'appraisal_contingency_duration',
            label: 'Appraisal Contingency',
            toolTipContent: 'A provision protecting the buyer by allowing them to cancel the contract if the property’s appraised value is lower than the agreed purchase price.',
        },
        {
            name: 'additional_notes',
            label: 'Additional Notes',
        },
    ];
    return _.keyBy(fields, 'name');
};
export const getSealedBidFormValues = (bid, viewingFrom) => {
    const fields = getSealedBidFormFields(viewingFrom);
    const values = utils.traditionalOffer.getOfferValues(bid);
    return {
        [fields.offer_amount.name]: {
            value: formatMoney(values.offer_amount),
            rawValue: values.offer_amount,
        },
        [fields.down_payment_amount.name]: {
            value: formatMoney(values.down_payment_amount),
            rawValue: values.down_payment_amount,
        },
        [fields.earnest_money_deposit_amount.name]: {
            value: formatMoney(values.earnest_money_deposit_amount),
            rawValue: values.earnest_money_deposit_amount,
        },
        [fields.payment_type.name]: {
            value: _.find(paymentTypeOptions, {
                value: values.payment_type,
            })?.text,
            rawValue: values.payment_type,
        },
        [fields.seller_concession_amount.name]: {
            value: values.seller_concession_amount
                ? formatMoney(values.seller_concession_amount)
                : '',
            rawValue: values.seller_concession_amount,
        },
        [fields.agent_commission_amount.name]: {
            value: formatMoney(values.commission?.[0]?.agent_commission_amount),
            rawValue: values.commission?.[0]?.agent_commission_amount,
        },
        [fields.paid_by.name]: {
            value: _.find(sellerBuyerOptions, {
                value: values.commission?.[0]?.paid_by,
            })?.text,
            rawValue: values.commission?.[0]?.paid_by,
        },
        [fields.close_escrow_duration.name]: {
            value: values.close_escrow_duration
                ? `${values.close_escrow_duration} Days from contract execution`
                : '',
            rawValue: values.close_escrow_duration,
        },
        [fields.leaseback.name]: {
            value: values.leaseback_duration
                ? `${formatMoney(values.leaseback_amount ?? 0)} per day for ${values.leaseback_duration} Days`
                : '',
            rawValue: {
                leaseback_amount: values.leaseback_amount,
                leaseback_duration: values.leaseback_duration,
            },
        },
        [fields.option_period.name]: {
            value: values.option_period_cost_amount && values.option_period_duration
                ? `${formatMoney(values.option_period_cost_amount)} per day for ${values.option_period_duration} Days`
                : '',
            rawValue: {
                option_period_cost_amount: values.option_period_cost_amount,
                option_period_duration: values.option_period_duration,
            },
        },
        [fields.inspection_contingency_period_duration.name]: {
            value: values.inspection_contingency_period_duration
                ? `${values.inspection_contingency_period_duration} Days`
                : 'Waived',
            rawValue: values.inspection_contingency_period_duration,
        },
        [fields.financing_contingency_duration.name]: {
            value: values.financing_contingency_duration
                ? `${values.financing_contingency_duration} Days`
                : 'Waived',
            rawValue: values.financing_contingency_duration,
        },
        [fields.appraisal_contingency_duration.name]: {
            value: values.appraisal_contingency_duration
                ? `${values.appraisal_contingency_duration} Days`
                : 'Waived',
            rawValue: values.appraisal_contingency_duration,
        },
        [fields.additional_notes.name]: {
            value: values.additional_notes,
            rawValue: values.additional_notes,
        },
    };
};
export const getSealedBidFormHeaders = (headers, hasCounterOffer, canUpdateMyOffer) => {
    let remainingHeaders = [...(headers || [])];
    const newHeaders = [remainingHeaders.shift() || 'My Offer'];
    if (hasCounterOffer) {
        newHeaders.push(remainingHeaders.shift() || 'Counteroffer');
    }
    if (canUpdateMyOffer) {
        const updateHeader = remainingHeaders.shift() || `Update My Offer`;
        newHeaders.push(hasCounterOffer
            ? `${remainingHeaders.shift() || `Update Offer Instead`}`
            : updateHeader);
    }
    if (remainingHeaders.length && newHeaders.length < 2) {
        newHeaders.push(remainingHeaders.shift() || '');
    }
    return newHeaders;
};
export const getSealedBidFormConfig = ({ listing, headers, hasCounterOffer, canUpdateMyOffer, viewingFrom, }) => {
    const fields = getSealedBidFormFields(viewingFrom);
    const configHeaders = getSealedBidFormHeaders(headers, hasCounterOffer, canUpdateMyOffer);
    const sellerTerms = listing?.seller_terms;
    const sections = [
        {
            name: 'Offer Details',
            fields: [
                fields.offer_amount,
                fields.payment_type,
                fields.down_payment_amount,
                fields.earnest_money_deposit_amount,
            ],
        },
        {
            name: 'Closing',
            fields: _.compact([
                fields.close_escrow_duration,
                sellerTerms?.leaseback_terms?.leaseback_period_in_days
                    ? fields.leaseback
                    : null,
                fields.seller_concession_amount,
                fields.agent_commission_amount,
                fields.paid_by,
            ]),
        },
        {
            name: 'Contingencies',
            fields: [
                sellerTerms?.inspection_terms?.is_option_contingency
                    ? fields.option_period
                    : fields.inspection_contingency_period_duration,
                fields.financing_contingency_duration,
                fields.appraisal_contingency_duration,
                fields.additional_notes,
            ],
        },
    ];
    return { sections, headers: configHeaders };
};
export const getOfferAndCounterOfferDifferences = (offer, counterOffer) => {
    return _.transform(offer, (result, value, key) => {
        if (!_.isEqual(value, counterOffer[key])) {
            result.push(key);
        }
    }, []);
};
const numbersOnly = (val) => {
    return typeof val === 'string' ? val && val.replace(/[^0-9]/g, '') : '';
};
export const numberNoDecimals = (val = '0') => {
    if (typeof val !== 'string') {
        return '0';
    }
    return numbersOnly(val.includes('.') ? val.substring(0, val.lastIndexOf('.')) : val);
};
const roundToNearestFraction = (value, roundFactor) => {
    const factor = 1 / roundFactor;
    return Math.round(value * factor) / factor;
};
export const selectValidator = (value) => {
    return !value ? 'Must select a value' : undefined;
};
export const zeroOrNegativeValidator = (isMoney = false, canBeNull = false) => (value) => {
    if (canBeNull && value === null) {
        return undefined;
    }
    return !value || value <= 0
        ? `Value must be greater than ${isMoney ? '$' : ''}0`
        : undefined;
};
export const lessOrEqualToPurchasePriceValidator = (totalPurchasePrice, percentage = 100) => (value) => {
    const nonStandardPercentage = percentage !== 100;
    const totalPurchasePriceAmount = totalPurchasePrice * (percentage / 100);
    const percentageMessage = nonStandardPercentage
        ? ` ${percentage}% of `
        : ' ';
    return totalPurchasePriceAmount &&
        (value ?? 0) > roundToNearestFraction(totalPurchasePriceAmount, 1)
        ? ` Value cannot exceed${percentageMessage}the Purchase Price `
        : '';
};
export const lessOrEqualsClosingDateValidator = (closeDateDuration) => (value) => {
    return closeDateDuration && (value ?? 0) > closeDateDuration
        ? ` Value cannot exceed your closing date `
        : '';
};
export const lessThanStartingBidValidator = (startingBidAmount) => (offerAmount) => {
    return startingBidAmount && (offerAmount ?? 0) < startingBidAmount
        ? ` Must be equal or greater than the starting bid `
        : '';
};
const numberMask = new utils.NumberMask({
    roundToNearestFraction: 1,
    allowNegative: false,
    integerLimit: 3,
});
const numberInputMask = (rawValue) => numberMask.mask(rawValue);
export const useSealedBidForm = ({ bid, listing, segment, headers, readOnly: readOnlyProp = false, counterOffer, canUpdateMyOffer = true, viewingFrom, }) => {
    const initialData = useMemo(() => utils.traditionalOffer.getOfferValues(bid), [bid]);
    const hasCounterOffer = !!counterOffer;
    const [data, setData] = React.useState(initialData);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [readOnly, setReadOnly] = useState(readOnlyProp);
    const reset = useCallback(() => {
        setData(initialData);
        setErrors({});
        setTouched(false);
        setDirty(false);
        setReadOnly(readOnlyProp);
    }, [initialData, readOnlyProp]);
    useEffect(() => {
        if (!dirty) {
            setData(initialData);
        }
    }, [initialData, dirty]);
    const startingBidAmount = segment?.starting_bid_amount || 0;
    const totalPurchasePrice = data?.offer_amount;
    const isOptionContingency = listing?.seller_terms?.inspection_terms?.is_option_contingency;
    const hasLeasebackPeriod = listing?.seller_terms?.leaseback_terms?.leaseback_period_in_days;
    const validators = useMemo(() => {
        const fields = getSealedBidFormFields(viewingFrom);
        return Object.keys(fields).reduce((acc, key) => {
            if (key === 'offer_amount') {
                acc[key] = [zeroOrNegativeValidator(true)];
            }
            else if (key === 'close_escrow_duration') {
                acc[key] = [zeroOrNegativeValidator()];
            }
            else if ([
                'down_payment_amount',
                'earnest_money_deposit_amount',
                'leaseback_amount',
            ].includes(key)) {
                acc[key] = [lessOrEqualToPurchasePriceValidator(totalPurchasePrice)];
            }
            else if (key === 'seller_concession_amount') {
                acc[key] = [lessOrEqualToPurchasePriceValidator(totalPurchasePrice, 9)];
            }
            else if ([
                'inspection_contingency_period_duration',
                'financing_contingency_duration',
                'appraisal_contingency_duration',
            ].includes(key)) {
                acc[key] = [
                    lessOrEqualsClosingDateValidator(data?.close_escrow_duration),
                ];
            }
            else if (hasLeasebackPeriod && key === 'leaseback') {
                acc['leaseback_amount'] = [
                    lessOrEqualToPurchasePriceValidator(totalPurchasePrice),
                ];
            }
            else if (key === 'agent_commission_amount') {
                acc['agent_commission_amount'] = [
                    lessOrEqualToPurchasePriceValidator(totalPurchasePrice),
                ];
            }
            else if (key === 'paid_by' &&
                !!data.commission?.[0]?.agent_commission_amount) {
                acc['paid_by'] = [selectValidator];
            }
            else if (isOptionContingency && key === 'option_period') {
                acc['option_period_duration'] = [
                    lessOrEqualsClosingDateValidator(data?.close_escrow_duration),
                ];
                acc['option_period_cost_amount'] = [
                    lessOrEqualToPurchasePriceValidator(totalPurchasePrice),
                ];
            }
            return acc;
        }, {});
    }, [
        startingBidAmount,
        totalPurchasePrice,
        data,
        hasLeasebackPeriod,
        isOptionContingency,
        viewingFrom,
    ]);
    const validateField = useCallback((value, name) => {
        let error = null;
        for (const validator of validators[name] || []) {
            if (!validator)
                continue;
            error = validator(value);
            if (error)
                break;
            else
                error = false;
        }
        return error;
    }, [validators]);
    const validate = useCallback(() => {
        const errors = _.mapValues(_.omit(data, 'commission'), (value, key) => validateField(value, key));
        if (!_.isEmpty(data?.commission)) {
            _.set(errors, 'agent_commission_amount', validateField(data?.commission?.[0]?.agent_commission_amount, 'agent_commission_amount'));
            _.set(errors, 'paid_by', validateField(data?.commission?.[0]?.paid_by, 'paid_by'));
        }
        const stringErrors = _.pickBy(errors, _.isString);
        setErrors(stringErrors);
        return _.isEmpty(stringErrors);
    }, [data, validateField, setErrors]);
    const onFieldChange = useCallback(e => {
        const { value, name, secondaryFieldNameToUpdate = null } = e.target;
        const updatedData = _.cloneDeep(data);
        if (['agent_commission_amount', 'paid_by'].includes(name)) {
            const firstCommission = updatedData?.commission?.[0];
            const agentCommissionAmount = name === 'agent_commission_amount'
                ? Number(value) || null
                : firstCommission?.agent_commission_amount;
            const paid_by = name === 'paid_by' ? value : firstCommission?.paid_by;
            const hasCommission = !!agentCommissionAmount;
            updatedData.is_agent_included = hasCommission;
            updatedData.commission = hasCommission
                ? [
                    {
                        agent_commission_amount: agentCommissionAmount,
                        paid_by: paid_by || null,
                    },
                ]
                : [];
            setErrors(_.omit(errors, ['paid_by']));
        }
        else {
            const defaultValue = name.includes('duration') ? null : value;
            _.set(updatedData, name, value || defaultValue);
            if (secondaryFieldNameToUpdate) {
                _.set(updatedData, secondaryFieldNameToUpdate, value || defaultValue);
            }
        }
        if (name === 'payment_type' && value === graph.enums.FinanceType.Cash) {
            updatedData.financing_contingency_duration = null;
            updatedData.appraisal_contingency_duration = null;
            updatedData.down_payment_amount = null;
        }
        else if (name === 'leaseback_amount' && _.isNil(value)) {
            updatedData.leaseback_duration = null;
        }
        const isDirty = !_.isEqual(updatedData, initialData);
        setData(updatedData);
        setTouched(true);
        setDirty(isDirty);
    }, [data, errors, initialData]);
    const onNumberFieldChange = useCallback((isMoney = false) => (e) => {
        const { value, name } = e.target;
        let amount;
        if (isMoney) {
            amount = value ? utils.currency.parse(e.target.value) : null;
        }
        else {
            amount = value ? parseInt(numberNoDecimals(value)) : 0;
        }
        onFieldChange({ target: { value: amount, name } });
    }, [onFieldChange]);
    const onFocus = useCallback(e => {
        const { name } = e.target;
        setErrors(_.omit(errors, [name]));
    }, [errors]);
    const onBlur = useCallback(e => {
        const { value, name } = e.target;
        const error = validateField(value, name);
        if (error) {
            setErrors({ ...errors, [name]: error });
        }
    }, [validateField, errors]);
    const onNumberFieldBlur = useCallback((canBeNull = false) => (e) => {
        const { value, name } = e.target;
        const defaultValue = canBeNull ? null : 0;
        const amount = value ? parseInt(numberNoDecimals(value)) : defaultValue;
        onBlur({ target: { value: amount, name } });
    }, [onBlur]);
    const toggleReadOnly = useMemo(() => hasCounterOffer && canUpdateMyOffer
        ? () => setReadOnly(!readOnly)
        : undefined, [readOnly, hasCounterOffer, canUpdateMyOffer]);
    const fieldMap = useMemo(() => {
        return {
            offer_amount: (_jsx(CurrencyInputField, { name: 'offer_amount', value: data.offer_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `offer_amount${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.offer_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly })),
            down_payment_amount: (_jsx(CurrencyInputField, { name: 'down_payment_amount', value: data.down_payment_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `down_payment_amount${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.down_payment_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly || data.payment_type === graph.enums.FinanceType.Cash })),
            earnest_money_deposit_amount: (_jsx(CurrencyInputField, { name: 'earnest_money_deposit_amount', value: data.earnest_money_deposit_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `earnest_money_deposit_amount_${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.earnest_money_deposit_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly })),
            payment_type: (_jsx(DropdownField, { value: data.payment_type, onChange: (value) => {
                    const valueObj = value
                        ? _.find(paymentTypeOptions, { value })
                        : null;
                    onFieldChange({
                        target: { value: valueObj?.value, name: 'payment_type' },
                    });
                }, options: paymentTypeOptions, placeholder: 'Payment Type', elmId: `payment_type_${listing?.listing_id}`, disabled: readOnly })),
            seller_concession_amount: (_jsx(CurrencyInputField, { name: 'seller_concession_amount', value: data.seller_concession_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `seller_concession_amount_${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.seller_concession_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly })),
            agent_commission_amount: (_jsx(CurrencyInputField, { name: 'agent_commission_amount', value: data?.commission?.[0]?.agent_commission_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `agent_commission_amount_${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.agent_commission_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly })),
            paid_by: (_jsx(DropdownField, { value: data?.commission?.[0]?.paid_by || null, onChange: (value) => {
                    const valueObj = value
                        ? _.find(sellerBuyerOptions, { value })
                        : null;
                    onFieldChange({
                        target: { value: valueObj?.value, name: 'paid_by' },
                    });
                }, options: sellerBuyerOptions, placeholder: 'Select', error: errors?.paid_by, touched: touched, elmId: `paid_by_${listing?.listing_id}`, disabled: readOnly || !data?.commission?.[0]?.agent_commission_amount })),
            close_escrow_duration: (_jsx(MaskedInputField, { inputMode: 'numeric', name: 'close_escrow_duration', value: data.close_escrow_duration, onChange: onNumberFieldChange(), onBlur: onNumberFieldBlur(), onFocus: onFocus, elmId: `close_escrow_date_${listing?.listing_id}`, placeholder: 'Number of days', error: errors?.close_escrow_duration, mask: numberInputMask, suffix: 'Days from contract execution', touched: touched, disabled: readOnly })),
            leaseback: (_jsx("div", { className: 'field-group-container', children: _jsxs(_Fragment, { children: [_jsxs("div", { className: 'field-group', children: [_jsx(CurrencyInputField, { name: 'leaseback_amount', value: data.leaseback_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: () => {
                                        setErrors(_.omit(errors, ['leaseback_amount', 'leaseback_duration']));
                                    }, elmId: `leaseback_amount_${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.leaseback_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly }), _jsx(Text, { className: 'u-pl-2 u-pr-2', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "FOR" }), _jsx(MaskedInputField, { inputMode: 'numeric', name: 'leaseback_duration', value: data.leaseback_duration, onChange: onNumberFieldChange(_.isNil(data.leaseback_amount)), onBlur: onNumberFieldBlur(_.isNil(data.leaseback_amount)), onFocus: onFocus, elmId: `leaseback_duration_${listing?.listing_id}`, placeholder: 'Number of days', touched: touched, mask: numberInputMask, suffix: 'Days', disabled: readOnly || _.isNil(data.leaseback_amount) })] }), errors?.leaseback_amount && (_jsx("div", { className: 'field-error', children: errors?.leaseback_amount })), !errors?.leaseback_amount && errors?.leaseback_duration && (_jsx("div", { className: 'field-error', children: errors?.leaseback_duration }))] }) })),
            option_period: (_jsxs("div", { className: 'field-group-container', children: [_jsxs("div", { className: 'field-group contingency-field', children: [_jsx(CurrencyInputField, { name: 'option_period_cost_amount', value: data.option_period_cost_amount, onChange: onNumberFieldChange(true), onBlur: onNumberFieldChange(true), onFocus: onFocus, elmId: `option_period_cost_amount${listing?.listing_id}`, placeholder: 'Enter amount here', error: errors?.option_period_cost_amount, touched: touched, mask: utils.currency.mask, allowDecimal: false, disabled: readOnly }), _jsx(Text, { className: 'u-pl-2 u-pr-2', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "FOR" }), _jsx(MaskedInputField, { inputMode: 'numeric', name: 'option_period_duration', value: data.option_period_duration, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `option_period_duration${listing?.listing_id}`, placeholder: 'Enter number of days', error: errors?.option_period_duration, touched: touched, mask: numberInputMask, suffix: 'Days', disabled: readOnly })] }), errors?.option_period_cost_amount && (_jsx("div", { className: 'field-error', children: errors?.option_period_cost_amount })), !errors?.option_period_cost_amount &&
                        errors?.option_period_duration && (_jsx("div", { className: 'field-error', children: errors?.option_period_duration })), _jsxs("div", { className: 'toggle-container', children: [_jsx(CheckboxField, { checked: data.option_period_duration === null, hideLabel: true, toggle: true, onChange: () => {
                                    const value = data.option_period_duration === null ? 1 : null;
                                    onFieldChange({
                                        target: {
                                            value,
                                            name: 'option_period_duration',
                                            secondaryFieldNameToUpdate: 'option_period_cost_amount',
                                        },
                                    });
                                }, disabled: readOnly }), _jsx(Text, { size: 'mini', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "Waive Option Period" })] })] })),
            inspection_contingency_period_duration: (_jsxs("div", { className: 'contingency-field', children: [_jsx(MaskedInputField, { inputMode: 'numeric', name: 'inspection_contingency_period_duration', value: data.inspection_contingency_period_duration, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `inspection_contingency_period_duration_${listing?.listing_id}`, placeholder: 'Enter number of days', error: errors?.inspection_contingency_period_duration, touched: touched, mask: numberInputMask, suffix: 'Days', disabled: readOnly }), _jsxs("div", { className: 'toggle-container', children: [_jsx(CheckboxField, { checked: data.inspection_contingency_period_duration === null, hideLabel: true, toggle: true, onChange: () => {
                                    const value = data.inspection_contingency_period_duration === null
                                        ? 1
                                        : null;
                                    onFieldChange({
                                        target: {
                                            value,
                                            name: 'inspection_contingency_period_duration',
                                        },
                                    });
                                }, disabled: readOnly }), _jsx(Text, { size: 'mini', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "Waive Inspection Contingency" })] })] })),
            financing_contingency_duration: (_jsxs("div", { className: 'contingency-field', children: [_jsx(MaskedInputField, { inputMode: 'numeric', name: 'financing_contingency_duration', value: data.financing_contingency_duration, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `financing_contingency_duration_${listing?.listing_id}`, placeholder: 'Enter number of days', error: errors?.financing_contingency_duration, touched: touched, mask: numberInputMask, suffix: 'Days', disabled: readOnly || data.payment_type === graph.enums.FinanceType.Cash }), _jsxs("div", { className: 'toggle-container', children: [_jsx(CheckboxField, { checked: data.financing_contingency_duration === null, hideLabel: true, toggle: true, onChange: () => {
                                    const value = data.financing_contingency_duration === null ? 1 : null;
                                    onFieldChange({
                                        target: { value, name: 'financing_contingency_duration' },
                                    });
                                }, disabled: readOnly || data.payment_type === graph.enums.FinanceType.Cash }), _jsx(Text, { size: 'mini', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "Waive Financing Contingency" })] })] })),
            appraisal_contingency_duration: (_jsxs("div", { className: 'contingency-field', children: [_jsx(MaskedInputField, { inputMode: 'numeric', name: 'appraisal_contingency_duration', value: data.appraisal_contingency_duration, onChange: onNumberFieldChange(true), onBlur: onNumberFieldBlur(true), onFocus: onFocus, elmId: `appraisal_contingency_duration_${listing?.listing_id}`, placeholder: 'Enter number of days', error: errors?.appraisal_contingency_duration, touched: touched, mask: numberInputMask, suffix: 'Days', disabled: readOnly || data.payment_type === graph.enums.FinanceType.Cash }), _jsxs("div", { className: 'toggle-container', children: [_jsx(CheckboxField, { checked: data.appraisal_contingency_duration === null, hideLabel: true, toggle: true, onChange: () => {
                                    const value = data.appraisal_contingency_duration === null ? 1 : null;
                                    onFieldChange({
                                        target: { value, name: 'appraisal_contingency_duration' },
                                    });
                                }, disabled: readOnly || data.payment_type === graph.enums.FinanceType.Cash }), _jsx(Text, { size: 'mini', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "Waive Appraisal Contingency" })] })] })),
            additional_notes: (_jsxs("div", { className: 'contingency-field', children: [_jsx(AdditionalNotesField, { elmId: `additional_notes_${listing?.listing_id}`, error: errors?.additional_notes, value: data.additional_notes, onChange: (_e, onChangeData) => {
                            onFieldChange({
                                target: {
                                    value: onChangeData.value,
                                    name: 'additional_notes',
                                },
                            });
                        }, onBlur: onBlur, onFocus: onFocus, touched: touched, readOnly: readOnly }), _jsxs("div", { className: 'toggle-container', children: [_jsx(CheckboxField, { checked: data.additional_notes === null, hideLabel: true, toggle: true, onChange: () => {
                                    const value = data.additional_notes === null ? '' : null;
                                    onFieldChange({
                                        target: { value, name: 'additional_notes' },
                                    });
                                }, disabled: readOnly }), _jsx(Text, { size: 'mini', color: readOnly ? ColorNames.GrayLight : ColorNames.Gray, children: "Remove Additional Notes" })] })] })),
        };
    }, [
        data,
        errors,
        touched,
        readOnly,
        listing,
        onFieldChange,
        onBlur,
        onNumberFieldChange,
        onNumberFieldBlur,
        setErrors,
    ]);
    const components = useMemo(() => canUpdateMyOffer
        ? (props) => _.get(fieldMap, props?.name) ?? null
        : undefined, [fieldMap, canUpdateMyOffer]);
    const config = useMemo(() => getSealedBidFormConfig({
        headers,
        listing,
        hasCounterOffer,
        canUpdateMyOffer,
        viewingFrom,
    }), [headers, listing, hasCounterOffer, canUpdateMyOffer, viewingFrom]);
    return {
        data,
        errors,
        touched,
        config,
        dirty,
        readOnly,
        components,
        reset,
        toggleReadOnly,
        validate,
    };
};
//# sourceMappingURL=sealedBid.js.map